import React from 'react';
import {Map, List} from 'immutable';
import type {
  Property,
  PropertyEnum,
  PropTable,
} from '@packages/systems/commerce/core';
import type {
  AtomRenderProps,
  ChildRender,
  ContextType,
} from '@packages/systems/wfdl';
import {
  DATA_ATTR_COMMERCE_PRODUCT_CURRENT_SKU_VALUES,
  DATA_ATTR_NODE_TYPE,
  DATA_ATTR_COMMERCE_PRODUCT_ID,
  DATA_ATTR_PRESELECT_DEFAULT_VARIANT,
  NODE_TYPE_COMMERCE_ADD_TO_CART_OPTION_LIST,
} from '@packages/systems/commerce/constants';
import {simplifySkuValues} from '@packages/shared/site';
import {withMeta, withSystem, Plugins, Atom} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  TListOfElement,
  TEnum,
  TTypeConstructor,
  TRecord,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  getBoolean,
  getChildren,
  getInPojo,
  getStringOrElse,
} from '@packages/utilities/data-utils';

const commerceLockedDynSrcType = qualify(
  Plugins.name('Commerce'),
  typeName('LockedDynSource')
);

const renderOption =
  ({
    context,
    renderChild,
    children,
    optionValues,
    data,
  }: {
    context: ContextType;
    renderChild: ChildRender;
    children: unknown[];
    optionValues: Record<string, unknown>;
    data: unknown;
  }) =>
  (
    option:
      | Property
      | {
          enum: Array<PropertyEnum>;
          id: string;
          name: string;
        },
    itemIndex: number
  ) => {
    const childKey = option.id || '';
    // TODO (ecommerce): Memoize commerce context mutations
    const newContext = context
      .set('data', {
        id: option.id,
        name: option.name,
        enum: option.enum,
      })
      .set('selected', optionValues[childKey])
      .set(
        'selectorType',
        getStringOrElse(getInPojo(data, ['selectorType']), 'select')
      );
    return children.map((child) =>
      renderChild(child, newContext, childKey, itemIndex)
    );
  };

const renderOptions = (
  {renderChild, context, data}: AtomRenderProps,
  optionSets: PropTable,
  optionValues: {
    [key: string]: string;
  }
) => {
  const renderOptionWithContext = renderOption({
    context,
    renderChild,
    children: getChildren(data),
    optionValues,
    data,
  });

  return optionSets.length > 0 ? optionSets.map(renderOptionWithContext) : null;
};

const staticBindings = Map({
  optionSets: List([
    Map({
      slug: 'sku-properties',
      type: 'CommercePropTable',
    }),
  ]),
  optionValues: List([
    Map({
      slug: 'default-sku',
      type: 'ItemRef',
    }),
    Map({
      slug: 'sku-values',
      type: 'CommercePropValues',
    }),
  ]),
  productId: List([
    Map({
      id: 'id',
      slug: 'id',
      type: 'PlainText',
    }),
  ]),
});

const CommerceOptionList = Atom.createRender((props) => {
  const {
    attributes,
    classes,
    generateAttributes,
    data,
    optionSets,
    optionValues,
    productId,
  } = props;
  if (optionSets && optionValues && productId) {
    const simplifiedOptionValues = simplifySkuValues(optionValues);
    const preselectDefaultVariant = getBoolean(
      getInPojo(data, ['preselectDefaultVariant'])
    );
    const newAttributes = attributes.merge(
      Map({
        // @TODO – remove this baking of sku values, as we now build this on the published page
        // i (zach) can't remove this right now, because if i did, then existing sites that haven't
        // published since the published site changes will break, since this atom would render without
        // this attribute immediately, but the old published site code will still exist
        [DATA_ATTR_COMMERCE_PRODUCT_CURRENT_SKU_VALUES]: encodeURIComponent(
          JSON.stringify(simplifiedOptionValues)
        ),
        [DATA_ATTR_NODE_TYPE]: NODE_TYPE_COMMERCE_ADD_TO_CART_OPTION_LIST,
        [DATA_ATTR_COMMERCE_PRODUCT_ID]: productId,
        ...(preselectDefaultVariant !== undefined && {
          [DATA_ATTR_PRESELECT_DEFAULT_VARIANT]:
            preselectDefaultVariant.toString(),
        }),
      })
    );

    return (
      <div {...generateAttributes(newAttributes, classes)} role="group">
        {renderOptions(props, optionSets, simplifiedOptionValues)}
      </div>
    );
  }
  return null;
});

const baseConfig = {
  displayName: 'Option List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  showEmpty: false,
  constraints: {
    wraps: ['CommerceAddToCartOption'],
    ancestors: [
      {
        is: 'CommerceAddToCartForm',
        rule: 'ExactlyOne',
      },
    ],
  },
  render: CommerceOptionList,
  staticBindings,
} as const;

export const CommerceAddToCartOptionListWithSelectorTypes =
  // @ts-expect-error - TS2345 - Argument of type '{ fields: { source: Type; selectorType: Type; preselectDefaultVariant: Type; search: Type; children: Type; }; displayName: "Option List"; icon: { readonly type: "Component"; readonly value: Qualified<...>; }; showEmpty: false; constraints: { ...; }; render: { ...; }; staticBindings: Map<...>; }' is not assignable to parameter of type 'AtomConfigShape'.
  createIntegratedAtom({
    ...baseConfig,
    fields: {
      source: withMeta(
        {source: 'Product Options'},
        TTypeConstructor(commerceLockedDynSrcType)
      ),
      selectorType: withSystem(
        {
          label: 'Variant selector',
          defaultValue: 'select',
        },
        TEnum({
          select: 'Select',
          pills: 'Buttons',
        })
      ),
      preselectDefaultVariant: withMeta(
        {
          labels: {
            true: 'Yes',
            false: 'No',
          },
        },
        withSystem(
          {label: 'Preselect default variant', defaultValue: false},
          TTypeConstructor(
            qualify(Plugins.name('Basic'), typeName('LabeledBoolean'))
          )
        )
      ),
      search: withSystem(
        {private: true},
        TRecord({
          exclude: excludeSearchByDefaultType,
        })
      ),
      children: TListOfElement,
    },
  });

// IMPORTANT: THE FOLLOWING ATOM CONFIG IS A LEGACY ATOM, DO NOT MODIFY
// We have to keep this near-copy of the atom above, because we have old option lists
// that do not have the pill selector atoms inside of them, and we did not run a migration
// to add those new atoms to all existing option lists. Since there's not a clean way to have
// data types render dynamically based on node data (and understandably so), the cleanest solution
// we came up with was to create a new atom, and have the legacy atom continue to exist for existing sites.
// This approach requiring users to create a new element to get the new selector type was agreed upon by
// Ecommerce's EPD stakeholders for this project (E: Zach B, P: Chloe Q, D: Maciej J), and the approach to create a new atom
// was discussed with Core in this thread: https://webflow.slack.com/archives/CHHHY5U1H/p1598563191051700
// @ts-expect-error - TS2345 - Argument of type '{ fields: { source: Type; preselectDefaultVariant: Type; children: Type; }; displayName: "Option List"; icon: { readonly type: "Component"; readonly value: Qualified<string>; }; showEmpty: false; constraints: { ...; }; render: { ...; }; staticBindings: Map<...>; }' is not assignable to parameter of type 'AtomConfigShape'.
export const LegacyCommerceAddToCartOptionList = createIntegratedAtom({
  ...baseConfig,
  fields: {
    source: withMeta(
      {source: 'Product Options'},
      TTypeConstructor(commerceLockedDynSrcType)
    ),
    preselectDefaultVariant: withMeta(
      {
        labels: {
          true: 'Yes',
          false: 'No',
        },
      },
      withSystem(
        {label: 'Preselect default variant', defaultValue: false},
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('LabeledBoolean'))
        )
      )
    ),
    children: TListOfElement,
  },
});
